import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal,
} from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatButtonModule } from "@angular/material/button";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { finalize } from "rxjs";
import { SALUTATIONS, USERNAME_REGEX, SalutationValue } from "../../../components/account-setup.constants";

export type AccountSetupMode = "create" | "update";

export interface AccountSetupDefaults {
  username?: string | null;
  language?: string | null;
  salutation?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface AccountSetupPayload {
  username: string;
  language?: string | null;
  salutation: SalutationValue;
  firstName: string;
  lastName: string;
}

export interface AccountSetupResponse {
  email: string | null;
  username: string | null;
  language: string | null;
  salutation: string | null;
  firstName: string | null;
  lastName: string | null;
  accountExists: boolean;
  profileComplete: boolean;
}

interface ErrorState {
  type: "key" | "message";
  value: string;
}

@Component({
  selector: "app-account-setup",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: "./account-setup.component.html",
  styleUrl: "./account-setup.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSetupComponent implements OnInit, OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  @Input({ required: true }) idToken!: string;
  @Input({ required: true }) languageOptions: string[] = [];
  @Input({ required: true }) translate!: (key: string, params?: Record<string, string | number>) => string;
  @Input() mode: AccountSetupMode = "create";
  @Input() defaults: AccountSetupDefaults = {};

  @Output() readonly completed = new EventEmitter<AccountSetupResponse>();

  readonly salutationOptions = SALUTATIONS;
  readonly submitting = signal(false);
  private readonly errorState = signal<ErrorState | null>(null);

  readonly form = this.fb.nonNullable.group({
    username: ["", [Validators.required, Validators.pattern(USERNAME_REGEX)]],
    salutation: ["", [Validators.required]],
    firstName: ["", [Validators.required, Validators.maxLength(100)]],
    lastName: ["", [Validators.required, Validators.maxLength(100)]],
    language: this.fb.control<string | null>(null),
  });

  readonly disableSubmit = computed(() => this.submitting());
  readonly errorMessage = computed(() => {
    const error = this.errorState();
    if (!error) {
      return null;
    }
    return error.type === "key" ? this.translate(error.value) : error.value;
  });

  ngOnInit(): void {
    this.applyDefaults();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["defaults"] && !changes["defaults"].firstChange) {
      this.applyDefaults();
    }
    if (changes["languageOptions"] && !changes["languageOptions"].firstChange) {
      const normalized = this.normalizeLanguage(this.form.controls.language.value);
      if (this.form.controls.language.value !== normalized) {
        this.form.controls.language.setValue(normalized, { emitEvent: false });
      }
    }
  }

  submit(): void {
    if (this.submitting()) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.errorState.set(null);
    this.submitting.set(true);

    const payload = this.buildPayload();
    const url = this.mode === "create" ? "/api/users" : "/api/profile";
    const method = this.mode === "create" ? "post" : "put";
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.idToken}` });

    const request$ =
      method === "post"
        ? this.http.post<AccountSetupResponse>(url, payload, { headers })
        : this.http.put<AccountSetupResponse>(url, payload, { headers });

    request$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.submitting.set(false)),
      )
      .subscribe({
        next: (response) => this.completed.emit(response),
        error: (error) => this.handleError(error),
      });
  }

  get languageList(): string[] {
    return Array.from(new Set(this.languageOptions)).sort();
  }

  getLanguageLabel(language: string): string {
    const key = `languages.${language}`;
    const label = this.translate(key);
    return label === key ? language.toUpperCase() : label;
  }

  controlHasError(controlName: keyof typeof this.form.controls, errorKey: string): boolean {
    const control = this.form.controls[controlName];
    return control.touched && control.hasError(errorKey);
  }

  private buildPayload(): AccountSetupPayload {
    const { username, salutation, firstName, lastName, language } = this.form.getRawValue();
    return {
      username: username.trim(),
      salutation: salutation as SalutationValue,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      language: language ? language.trim() : null,
    };
  }

  private handleError(error: unknown): void {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 409) {
        this.errorState.set({ type: "key", value: "accountSetup.errors.usernameExists" });
        return;
      }
      if (error.status === 400) {
        const message =
          typeof error.error === "string" && error.error.trim().length > 0
            ? error.error
            : this.translate("accountSetup.errors.validationFailed");
        this.errorState.set({ type: "message", value: message });
        return;
      }
      if (error.status === 404) {
        this.errorState.set({ type: "key", value: "accountSetup.errors.profileMissing" });
        return;
      }
    }
    this.errorState.set({ type: "key", value: "accountSetup.errors.generic" });
  }

  private normalizeLanguage(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    const normalized = value.trim().toLowerCase();
    return this.languageOptions.includes(normalized) ? normalized : null;
  }

  private applyDefaults(): void {
    const { username, salutation, firstName, lastName, language } = this.defaults;
    if (username !== undefined) {
      this.form.controls.username.setValue(username ?? "", { emitEvent: false });
    }
    if (salutation !== undefined) {
      this.form.controls.salutation.setValue(salutation ?? "", { emitEvent: false });
    }
    if (firstName !== undefined) {
      this.form.controls.firstName.setValue(firstName ?? "", { emitEvent: false });
    }
    if (lastName !== undefined) {
      this.form.controls.lastName.setValue(lastName ?? "", { emitEvent: false });
    }
    const normalizedLanguage = this.normalizeLanguage(language ?? null);
    this.form.controls.language.setValue(normalizedLanguage, { emitEvent: false });
  }
}
